import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router'
import { Button } from 'react-bootstrap'
import * as s from './RegularSeason.scss'
import { setDashboardTeam, getLeagueScoreboard, setPickTeamId,
    getTeamProfile, divisionStandings, findMyFootballMatchups } from '@severed-links/common.redherrings-reducers/football'
import { find, some, first, uniq } from 'lodash'
import DivisionStandings from '../DivisionStandings'
import DeadlineAlertContainer from '../DeadlineAlertContainer'
import LeagueScoreboardGame from './LeagueScoreboardGame'
import moment from 'moment-timezone'
import durationFormat from 'moment-duration-format'
import AvatarNeedsEditingAlert from '../TeamProfile/AvatarNeedsEditingAlert'
import pluralize from 'pluralize'
import UnpaidAlert from '../UnpaidAlert'
import { teamUrl, playerUrl } from '@severed-links/common.redherrings-constants'
import animateScheduleChange from './animateScheduleChange'

const RegularSeason = () => {

    const dispatch = useDispatch()
    const params = useParams()
    const playerId = useSelector(state => state.account._id)
    const seasonName = params.seasonName || null
    const seasons = useSelector(state => state.football.seasons)
    const season = seasons.find(i => i.seasonName === seasonName)
    const seasonId = (season || {}).seasonId || null
    const week = useSelector(state => state.football.week)
    const pickDeadline = useSelector(state => state.football.pickDeadline)
    const dashboard = useSelector(state => state.football.dashboard) || {}
    const myTeams = useSelector(state => state.football.myTeams) || []
    /* TODO: convert to existing data */
    const teamProfiles = useSelector(state => state.football.teamProfiles)
    const leagueStandings = useSelector(state => state.football.leagueStandings)
    const leagueScoreboards = useSelector(state => state.football.leagueScoreboards)
    const [activeTab, setActiveTab] = useState(null)
    const [showAllWeeks, setShowAllWeeks] = useState(false)
    const scheduleGrid = useRef(null)
    const cancelScheduleAnimation = useRef(() => {})

    useEffect(() => { !myTeams.some(team => team._id === activeTab) && myTeams.length ? setActiveTab((first(myTeams) || {})._id) : null }, [myTeams.length, activeTab])
    useEffect(() => { updateDashboardData() }, [seasonId, week, myTeams.length, dashboard._id])
    useEffect(() => { if (!myTeams.some(team => team._id === dashboard._id)) updateDashboardTeam((first(myTeams) || {})._id || null) }, [myTeams.length, dashboard._id])

    const updateDashboardTeam = teamId => {
        if (some(myTeams, { _id: teamId })) {
            dispatch(setDashboardTeam(find(myTeams, { _id: teamId })))
        }
    }
    
    const updateDashboardData = () => {
        if (seasonId && dashboard && dashboard._id && myTeams && myTeams.length) {
            dispatch(divisionStandings(seasonId))
            dispatch(getLeagueScoreboard(seasonName, week))

            const _loadedTeamProfiles = (teamProfiles || []).map(i => i._id)
            const _teamIds = (myTeams || []).map(i => i._id)
            const _unloadedTeamIds = _teamIds.filter(i => _loadedTeamProfiles.indexOf(i) === -1)
            Promise.all(_unloadedTeamIds.map(i => dispatch(getTeamProfile(i))))
        }
    }

    const findMyTeamProfiles = teamIds => (teamProfiles || []).filter(tp => teamIds.indexOf(tp._id) > -1)

    const findMyStandings = teamIds => {
        const leagueStanding = find(leagueStandings, { seasonName: seasonName })
        if (leagueStanding && leagueStanding.leagueStandings) {
            const _standings = leagueStanding.leagueStandings || []
            const _myDivisions = uniq(_standings.filter(i => (i.standings || []).some(x => teamIds.indexOf(x.teamId) > -1)).map(i => i.divisionCode))
            return _standings.filter(i => _myDivisions.indexOf(i.divisionCode) > -1)
        } else {
            return null
        }
    }

    const myGames = findMyFootballMatchups({ leagueScoreboards, seasons, seasonName, week, playerId })
    const myIds = myTeams.map(i => i._id)
    const _teamProfiles = findMyTeamProfiles(myIds) || []
    const _standings = findMyStandings(myIds)
    const selectedProfile = _teamProfiles.find(team => team._id === activeTab) || _teamProfiles[0]
    const schedule = selectedProfile?.seasonSchedule || []
    const recentAndUpcoming = schedule.filter(game => game.week >= Math.max(1, week - 1))
    const visibleSchedule = showAllWeeks ? schedule : (recentAndUpcoming.length ? recentAndUpcoming : schedule).slice(0, 4)
    useEffect(() => () => cancelScheduleAnimation.current(), [selectedProfile?._id, week])

    const toggleSchedule = () => {
        cancelScheduleAnimation.current()
        cancelScheduleAnimation.current = animateScheduleChange(scheduleGrid.current, () => {
            flushSync(() => setShowAllWeeks(value => !value))
        })
    }
    return (
        seasonName && week > 0 ?
        <div className={s.container}>
            <header className={s.heading}>
                <div>
                    <p className={s.eyebrow}>{seasonName} · Regular season</p>
                    <h1>Week {week}</h1>
                    <p>Your matchups, picks, and season at a glance.</p>
                </div>
                <Link className={s.standingsLink} to={`/football/league-standings/${seasonName}`}>Full league standings →</Link>
            </header>
            <div className={s.dashboardGrid}>
                <main className={s.main}>
                    {_teamProfiles.map(teamProfile =>
                        <AvatarNeedsEditingAlert _id={teamProfile._id} key={teamProfile._id}
                            name={teamProfile.teamName} isTeam avatarUrl={teamProfile.teamAvatarUrl} />
                    )}
                    {myTeams.map(team => <UnpaidAlert myTeam={team} key={team._id} />)}
                    <section aria-label='Your weekly matchups'>
                        <h2>Your {pluralize('matchup', myGames.length)}</h2>
                        {myGames.length ? myGames.map(myGame =>
                            <article className={s.matchup} key={`${myGame.game}-${myGame._id1}-${myGame._id2}`}>
                                <LeagueScoreboardGame seasonName={seasonName} game={myGame} />
                                <DeadlineAlertContainer compact deadline={pickDeadline}
                                    teamId={myGame[`_id${myGame.myTeam}`]}
                                    teamName={myGame[`name${myGame.myTeam}`]}
                                    setPickTeamId={setPickTeamId} showPickButton />
                            </article>
                        ) : <p className={s.empty}>No matchup is available for this week yet.</p>}
                    </section>
                    {selectedProfile ? <section className={s.schedule} aria-label='Season schedule'>
                        <div className={s.sectionHeading}>
                            <h2>Season schedule</h2>
                            {schedule.length > 4 ? <Button variant='link' onClick={toggleSchedule} aria-expanded={showAllWeeks}>
                                {showAllWeeks ? 'Show recent & upcoming' : 'View all weeks'}
                            </Button> : null}
                        </div>
                        {_teamProfiles.length > 1 ? <label className={s.teamSelector}>
                            Team
                            <select value={selectedProfile._id} onChange={event => setActiveTab(event.target.value)}>
                                {_teamProfiles.map(team => <option key={team._id} value={team._id}>{team.teamName}</option>)}
                            </select>
                        </label> : <p>{selectedProfile.teamName}</p>}
                        <div className={s.scheduleGrid} ref={scheduleGrid}>
                            {visibleSchedule.map(game => <Link
                                key={`${game.week}-${game.game}`}
                                data-schedule-key={`${game.week}-${game.game}`}
                                className={`${s.scheduleGame} ${game.week === week && !game.isWeekComplete ? s.currentWeek : ''} ${game.isWin ? s.win : game.isLoss ? s.loss : game.isTie ? s.tie : ''}`}
                                to={`/football/regular-season/match-up/${seasonName}/week/${game.week}/game/${game.game}/${encodeURIComponent(selectedProfile.teamName || '')}-vs-${encodeURIComponent(game.oppTeamName || '')}`}>
                                <span className={s.scheduleWeek}>Week {game.week}{game.week === week ? ' · This week' : ''}</span>
                                <div className={s.scheduleOpponent}>
                                    <img className={s.scheduleTeamAvatar} src={teamUrl(game.oppAvatarUrl)} alt='' loading='lazy' />
                                    <div className={s.scheduleIdentity}>
                                        <strong>{game.oppTeamName || 'Opponent to be announced'}</strong>
                                        <div className={s.schedulePlayer}>
                                            <img src={playerUrl(game.oppPlayerAvatarUrl)} alt='' loading='lazy' />
                                            <span>{game.oppPlayerName || 'Player to be announced'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={s.scheduleFooter}>
                                    {game.displayResult ? <strong className={s.scheduleResult}>{game.displayResult}</strong> : null}
                                    <span>View matchup →</span>
                                </div>
                            </Link>)}
                        </div>
                        {!schedule.length ? <p className={s.empty}>Your season schedule is not available yet.</p> : null}
                    </section> : null}
                </main>
                <aside className={s.standings} aria-label='Your division standings'>
                    <h2>Your {pluralize('division', (_standings || []).length)}</h2>
                    {(_standings || []).map(division => <DivisionStandings key={division.divisionCode}
                        division={{ divisionName: division.divisionName, divisionCode: division.divisionCode, standings: division.standings }}
                        playerId={playerId} shortForm />)}
                    {!_standings?.length ? <p className={s.empty}>Division standings will appear here when available.</p> : null}
                </aside>
            </div>
        </div>
        : null
    )
}

export default RegularSeason
